import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';

import { Card } from '../Card';
import { StarIcon } from '../../Icons';
import { Button } from '../Button';

import { formatAmount } from '../../helpers/shared';

type Props = {
  endDate: number;
  email: string;
  plan: {
    amount: number;
    interval: string;
    isTrial: boolean;
  };
  isCanceled: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onClose: () => void;
  onRenewal: () => void;
};

const SubscriptionModal = ({ isOpen, endDate, isCanceled, email, plan, onClose, onRenewal, onCancel }: Props) => {
  const isActive = endDate > Date.now();

  const getDaysLeft = () => {
    const diffTime = Math.abs(endDate - Date.now());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const daysLeft = isActive ? getDaysLeft() : 0;

  const cancelSubscription = async () => {
    const data = {
      email,
    };

    await fetch('/.netlify/functions/cancel-subscription', {
      method: 'POST',
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then(() => onCancel());
  };

  const handleButtonClick = () => {
    if (isCanceled) {
      onRenewal();
      mixpanel.track('Renewal subscription', {
        premium: true,
        daysLeft: getDaysLeft(),
        trial: plan.isTrial,
      });
    } else {
      cancelSubscription();
      mixpanel.track('Canceled Subscription');
    }
  };

  useEffect(() => {
    if (isOpen) {
      mixpanel.track('Premium opened', { premium: true, daysLeft: getDaysLeft(), trial: plan.isTrial });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
              relative 
            inline-block 
            overflow-hidden 
            transform 
            transition-all s
            align-middle 
            w-[360px]
            `}
            >
              <Card theme='blue' size='small' onClose={onClose}>
                <div className='relative text-left text-white'>
                  <p className='text-xl leading-xl font-bold mb-10'>Manage your premium</p>
                  <div className='max-w-[240px]'>
                    <StarIcon width={79} height={79} />
                    <p className='text-[40px] leading-[50px] font-bold mt-1.5 mb-2'>Days left: {daysLeft}</p>
                    {!isCanceled ? (
                      <>
                        <p className='text-sm leading-sm mb-1'>
                          Your subscription is active. It will renew automatically.
                        </p>
                        <p className='text-sm leading-sm mb-6'>
                          Plan: {formatAmount(plan.amount)} / {plan.interval}
                        </p>
                      </>
                    ) : (
                      <p className='text-sm leading-sm mb-6'>Your subscription is inactive</p>
                    )}

                    <p className={`text-white ${!isCanceled ? 'mb-20' : 'mb-32'}`}>
                      <a
                        className='underline hover:no-underline'
                        // eslint-disable-next-line max-len
                        href='https://endurable-weaver-d97.notion.site/Terms-Conditions-Tools-fbdcabcb12d942948055c022c3cf226b'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Terms of Use
                      </a>{' '}
                      &amp;{' '}
                      <a
                        className='underline hover:no-underline'
                        // eslint-disable-next-line max-len
                        href='https://endurable-weaver-d97.notion.site/Privacy-Policy-Tools-Products-87ad109325ae476a903172a44f41b393'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Privacy Policy
                      </a>
                    </p>
                  </div>
                  <Button theme='light' size='medium' onClick={handleButtonClick}>
                    {!isCanceled ? 'Cancel subscription' : 'Renewal subscription'}
                  </Button>
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export { SubscriptionModal };
